import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { PaymentRequest } from '@/types/database/payment-requests';

interface ApproverInfo {
  approval_level: number;
  max_approval_amount: number | null;
}

interface CanApproveResult {
  canApprove: boolean;
  approverInfo: ApproverInfo | null;
  isLoading: boolean;
  reason: string | null;
}

export const useCanApproveRequest = (request: PaymentRequest | null): CanApproveResult => {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['can-approve-request', request?.id, user?.id],
    queryFn: async () => {
      if (!user?.id || !request) {
        return { canApprove: false, approverInfo: null, reason: 'No user or request' };
      }

      // Only check for pending_approval status
      if (request.status !== 'pending_approval') {
        return { canApprove: false, approverInfo: null, reason: 'Request is not pending approval' };
      }

      // Check if user is a designated approver for this department (single-level)
      const { data: approver, error } = await supabase
        .from('department_approvers')
        .select('approval_level, max_approval_amount')
        .eq('department_id', request.department_id)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('Error checking approver:', error);
        return { canApprove: false, approverInfo: null, reason: 'Error checking approval rights' };
      }

      if (!approver) {
        return { canApprove: false, approverInfo: null, reason: 'You are not a designated approver for this department' };
      }

      // Check max approval amount
      if (approver.max_approval_amount && request.total_amount > approver.max_approval_amount) {
        return { 
          canApprove: false, 
          approverInfo: approver,
          reason: `Amount exceeds your approval limit (ETB ${approver.max_approval_amount.toLocaleString()})`
        };
      }

      return { canApprove: true, approverInfo: approver, reason: null };
    },
    enabled: !!user?.id && !!request && request.status === 'pending_approval',
  });

  return {
    canApprove: data?.canApprove ?? false,
    approverInfo: data?.approverInfo ?? null,
    isLoading,
    reason: data?.reason ?? null,
  };
};
